import React, { useMemo, useState } from 'react';
import { FormDialog, InputField, MuiBox } from '@kis-technologies/kis-ui';
import { doesFormHaveError, doesFormHaveInitialValues, onInputBlur, onValueChange } from 'utils/forms';
import { IntegrationForm, IntegrationFormOverlayProps } from '../types';
import { getIntegrationForm, integrationValidationSchema } from '../utils';

const IntegrationFormOverlay = ({ integration, onClose, onSubmit }: IntegrationFormOverlayProps) => {
  const initialForm = useMemo(() => getIntegrationForm(integration), [integration]);
  const [form, setForm] = useState<IntegrationForm>(initialForm);

  const isSaveDisabled = useMemo(
    () =>
      !form.name.value.trim() ||
      !form.code.value.trim() ||
      doesFormHaveError(form) ||
      // Nothing to save when editing without changes
      (Boolean(integration) && doesFormHaveInitialValues(form)),
    [form, integration]
  );

  return (
    <FormDialog
      open
      title={integration ? 'Edit Integration' : 'Create Integration'}
      confirmButtonText="Save"
      confirmButtonAction={() => onSubmit({ name: form.name.value.trim(), code: form.code.value.trim() })}
      confirmButtonDisabled={isSaveDisabled}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
    >
      <MuiBox py="16px" width="524px" display="flex" flexDirection="column" gap="32px">
        {/* Each field is wrapped so the gap only applies between fields, not between a label and its input */}
        <MuiBox>
          <InputField
            autoFocus
            data-testid="integration-name-input"
            error={form.name.error !== null}
            fullWidth
            helperText={form.name.error}
            inputProps={{ maxLength: 255 }}
            label="Name"
            onBlur={(e) => onInputBlur(e, 'name', integrationValidationSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'name', setForm)}
            size="small"
            value={form.name.value}
          />
        </MuiBox>
        <MuiBox>
          <InputField
            data-testid="integration-code-input"
            error={form.code.error !== null}
            fullWidth
            helperText={form.code.error}
            inputProps={{ maxLength: 255 }}
            label="Code"
            onBlur={(e) => onInputBlur(e, 'code', integrationValidationSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'code', setForm)}
            size="small"
            value={form.code.value}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};

export default IntegrationFormOverlay;
